// Invoice generation utilities
// TODO: Implement actual invoice generation and printing functionality

export interface InvoiceData {
  type: 'rental' | 'sale';
  invoiceNumber: string;
  date: Date;
  customer: {
    name: string;
    phone?: string;
    email?: string;
    license?: string;
  };
  car: {
    brand: string;
    model: string;
    plate?: string;
    year?: number;
    color?: string;
  };
  // Rental specific
  rentalPeriod?: {
    startDate: Date;
    endDate: Date;
    totalDays: number;
  };
  mileage?: {
    start: number;
    end: number;
    driven: number;
  };
  // Sale specific
  salePrice?: number;
  listedPrice?: number;
  // Common
  totalAmount: number;
  additionalCharges?: Array<{
    description: string;
    amount: number;
  }>;
}

/**
 * Generate invoice number
 * @param type - Type of invoice (rental or sale)
 * @returns Generated invoice number
 */
export const generateInvoiceNumber = (type: 'rental' | 'sale'): string => {
  const prefix = type === 'rental' ? 'RNT' : 'SAL';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.random().toString(36).substr(2, 3).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

/**
 * Print rental invoice
 * @param invoiceData - Invoice data for rental
 */
export const printRentalInvoice = (invoiceData: InvoiceData): void => {
  // TODO: Implement actual invoice printing
  console.log('Printing rental invoice:', invoiceData);
  alert(`Rental Invoice ${invoiceData.invoiceNumber}\n\nThis feature will be implemented to generate and print a professional invoice with:\n- Company details\n- Customer information\n- Car details\n- Rental period\n- Charges breakdown\n- Total amount`);
};

/**
 * Print sale invoice
 * @param invoiceData - Invoice data for sale
 */
export const printSaleInvoice = (invoiceData: InvoiceData): void => {
  // TODO: Implement actual invoice printing
  console.log('Printing sale invoice:', invoiceData);
  alert(`Sale Invoice ${invoiceData.invoiceNumber}\n\nThis feature will be implemented to generate and print a professional invoice with:\n- Company details\n- Customer information\n- Car details\n- Sale price\n- Payment details\n- Transfer information`);
};

/**
 * Generate invoice data from rental record
 * @param rentalRecord - Rental record data
 * @param carInfo - Car information
 * @returns Invoice data
 */
export const createRentalInvoiceData = (
  rentalRecord: any,
  carInfo: any
): InvoiceData => {
  return {
    type: 'rental',
    invoiceNumber: generateInvoiceNumber('rental'),
    date: new Date(),
    customer: {
      name: rentalRecord.customerName || 'N/A',
      phone: rentalRecord.customerContact,
      email: rentalRecord.customerEmail,
      license: rentalRecord.customerLicense,
    },
    car: {
      brand: carInfo.brand,
      model: carInfo.model,
      plate: carInfo.plate,
      year: carInfo.year,
      color: carInfo.color,
    },
    rentalPeriod: {
      startDate: new Date(rentalRecord.startDate),
      endDate: new Date(rentalRecord.endDate),
      totalDays: rentalRecord.totalDays,
    },
    mileage: {
      start: rentalRecord.startMileage,
      end: rentalRecord.endMileage,
      driven: rentalRecord.endMileage - rentalRecord.startMileage,
    },
    totalAmount: rentalRecord.totalAmount + (rentalRecord.additionalCharges?.reduce((sum: number, charge: any) => sum + charge.amount, 0) || 0),
    additionalCharges: rentalRecord.additionalCharges,
  };
};

/**
 * Generate invoice data from sale record
 * @param saleRecord - Sale record data
 * @param carInfo - Car information
 * @returns Invoice data
 */
export const createSaleInvoiceData = (
  saleRecord: any,
  carInfo: any
): InvoiceData => {
  return {
    type: 'sale',
    invoiceNumber: generateInvoiceNumber('sale'),
    date: new Date(),
    customer: {
      name: saleRecord.customerName || 'N/A',
      phone: saleRecord.customerPhone,
      license: saleRecord.customerLicense,
    },
    car: {
      brand: carInfo.brand,
      model: carInfo.model,
      plate: carInfo.plate,
      year: carInfo.year,
      color: carInfo.color,
    },
    salePrice: saleRecord.finalSalePrice || saleRecord.salePrice,
    listedPrice: saleRecord.originalPrice,
    totalAmount: saleRecord.finalSalePrice || saleRecord.salePrice,
  };
};
